import { createElement, ReactNode } from 'react';

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'footnote'
  | 'footnote-head'
  | 'label'
  | 'navigation'
  | 'data'
  | 'valuation';

type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'legend'
  | 'caption'
  | 'figcaption'
  | 'strong'
  | 'em'
  | 'small'
  | 'blockquote'
  | 'cite'
  | 'q'
  | 'abbr'
  | 'time'
  | 'code'
  | 'pre'
  | 'dt'
  | 'dd';

type Alignment = 'left' | 'right' | 'center';
type Margin = 'none' | 'xs' | 'sm' | 'md' | 'lg';

interface TypographyProps {
  /**
   * Typography variant that determines the default styling
   * @default 'body'
   */
  variant?: TypographyVariant;
  /**
   * HTML element to render. Overrides the default element for the variant.
   * Useful for maintaining semantic HTML while using different styles.
   */
  component?: TextElement;
  /**
   * Text alignment
   */
  align?: Alignment;
  /**
   * Margin bottom size. Overrides the default margin for the variant.
   * @default undefined (uses variant's default margin)
   */
  margin?: Margin;
  /**
   * Additional CSS classes to apply
   */
  className?: string;
  /**
   * Content to render
   */
  children: ReactNode;
}

// Variant to default element mapping
const defaultElements: Record<TypographyVariant, TextElement> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'p',
  footnote: 'p',
  'footnote-head': 'p',
  label: 'span',
  navigation: 'span',
  data: 'span',
  valuation: 'p',
};

// Variant styling classes based on Sketch specifications
const variantStyles: Record<TypographyVariant, string> = {
  // H1: Serif Regular, 28px mobile / 64px desktop
  h1: 'font-serif font-normal text-[1.75rem] leading-[1.286] md:text-[4rem] md:leading-[1.185]',

  // H2: Serif Bold, 20px mobile / 42px desktop
  h2: 'font-serif font-bold text-[1.25rem] leading-[1.4] md:text-[2.625rem] md:leading-[1.286]',

  // H3: Sans-serif Bold, 18px mobile / 24px desktop
  h3: 'font-sans font-bold text-[1.125rem] leading-[1.333] md:text-[1.5rem] md:leading-[1.333]',

  // H4: Sans-serif Regular, 18px mobile / 24px desktop
  h4: 'font-sans font-normal text-[1.125rem] leading-[1.125] md:text-[1.5rem] md:leading-[1.333]',

  // H5: Sans-serif Bold, 14px mobile / 18px desktop
  h5: 'font-sans font-bold text-[0.875rem] leading-[1.375] md:text-[1.125rem] md:leading-[1.333]',

  // H6: Sans-serif Bold, 14px mobile / 16px desktop
  h6: 'font-sans font-bold text-[0.875rem] leading-[1.57] md:text-[1rem] md:leading-[1.375]',

  // Body: Sans-serif Regular, 14px mobile / 16px desktop
  body: 'font-sans font-normal text-[0.875rem] leading-[1.57] md:text-[1rem] md:leading-[1.75]',

  // Footnote: Sans-serif Regular, 12px mobile / 14px desktop
  footnote:
    'font-sans font-normal text-[0.75rem] leading-[1.5] md:text-[0.875rem] md:leading-[1.57]',

  // Footnote Head: Sans-serif Regular, 14px mobile / 18px desktop
  'footnote-head':
    'font-sans font-normal text-[0.875rem] leading-[1.285] md:text-[1.125rem] md:leading-[1.222]',

  // Label: Sans-serif Medium, 14px both
  label: 'font-sans font-medium text-[0.875rem] leading-[1.57]',

  // Navigation: Sans-serif Medium (all caps), 14px both
  navigation: 'font-sans font-medium text-[0.875rem] leading-[1.285] uppercase',

  // Data: Sans-serif Medium, 14px both
  data: 'font-sans font-medium text-[0.875rem] leading-[1.57]',

  // Valuation: Sans-serif Light, 36px mobile / 60px desktop
  valuation:
    'font-sans font-light text-[2.25rem] leading-[1.333] md:text-[3.75rem] md:leading-[1.333]',
};

// Default margin-bottom values from Sketch (in rem equivalent of px values)
const defaultMargins: Record<TypographyVariant, string> = {
  h1: 'mb-0', // Auto in Sketch
  h2: 'mb-0', // Auto in Sketch
  h3: 'mb-[0.375rem]', // 6px / 16 = 0.375rem
  h4: 'mb-[0.375rem]', // 6px / 16 = 0.375rem
  h5: 'mb-[0.375rem]', // 6px / 16 = 0.375rem
  h6: 'mb-[0.375rem]', // 6px / 16 = 0.375rem
  body: 'mb-[0.375rem]', // 6px / 16 = 0.375rem
  footnote: 'mb-0', // Auto in Sketch
  'footnote-head': 'mb-0', // Auto in Sketch
  label: 'mb-0', // Auto in Sketch
  navigation: 'mb-0', // None
  data: 'mb-0', // None
  valuation: 'mb-0', // Auto in Sketch
};

// Margin size to Tailwind class mapping
const marginClasses: Record<Margin, string> = {
  none: 'mb-0',
  xs: 'mb-2', // 0.5rem / 8px
  sm: 'mb-4', // 1rem / 16px
  md: 'mb-6', // 1.5rem / 24px
  lg: 'mb-8', // 2rem / 32px
};

// Alignment to Tailwind class mapping
const alignmentClasses: Record<Alignment, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/**
 * Typography component that implements the design system's type styles
 *
 * @example
 * ```tsx
 * // Default h1 element with h1 styling
 * <Typography variant="h1">Page Header</Typography>
 *
 * // h1 styling on h3 element (for SEO/accessibility)
 * <Typography variant="h1" component="h3">Styled Header</Typography>
 *
 * // Centered with custom margin
 * <Typography variant="body" align="center" margin="lg">
 *   Centered body text
 * </Typography>
 *
 * // Navigation with custom classes
 * <Typography variant="navigation" className="text-blue-500">
 *   Nav Item
 * </Typography>
 * ```
 */
function Typography({
  variant = 'body',
  component,
  align,
  margin,
  className = '',
  children,
}: TypographyProps) {
  // Determine which element to render
  const elementType = component || defaultElements[variant] || 'p';

  // Build className string
  const classes = [
    variantStyles[variant],
    margin !== undefined ? marginClasses[margin] : defaultMargins[variant],
    align ? alignmentClasses[align] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const props = {
    className: classes,
  };

  // Create the element dynamically
  return createElement(elementType, props, children);
}

export default Typography;
